import { DotMatrix } from '@clickguard/design-system'
import { ReactNode } from 'react'
import { RiCheckLine } from 'react-icons/ri'
import { GButton } from 'components/basic-blocks'

// Single source of truth for the wizard's wording, so the settings and onboarding wrappers can't
// drift apart. The wrappers own layout/typography; everything the merchant reads lives here.
export const SHOPIFY_COPY = {
	install: {
		title: 'Install the ClickGUARD app',
		blurb:
			'Install the ClickGUARD app from the Shopify App Store and approve access in the tab that opens — we set up conversion tracking for this asset automatically, no store domain or code needed.',
		button: 'Install app',
		reconnectBlurb: 'Your Shopify access expired. Reconnect to resume conversion tracking.',
		reconnectButton: 'Reconnect',
		waiting: 'Waiting for you to install and approve access in the Shopify tab…',
		done: 'App installed'
	},
	embed: {
		title: 'Turn on the tracking app-embed',
		steps: [
			<>
				Open the theme editor below — the <span className="font-semibold">ClickGUARD Tracking</span> app-embed is
				switched on for you automatically.
			</>,
			<>
				If it isn&apos;t already on, toggle it on, then click <span className="font-semibold">Save</span> and close that
				tab.
			</>
		] as ReactNode[],
		note: 'The app-embed only adds the ClickGUARD tracking script to your storefront. It doesn’t change your theme, layout, or checkout, and won’t affect any orders.',
		button: 'Open theme editor',
		waiting: 'Waiting for you to enable the embed and Save in the Shopify tab…',
		done: 'Tracking embed enabled',
		todo: 'Available once the app is installed.'
	},
	connected: {
		title: 'Shopify connected successfully',
		blurb: (shop: string) =>
			`Conversions from ${shop} are tracked automatically by the ClickGUARD app — no manual code needed.`
	},
	cancel: 'Cancel'
}

export type StepState = 'todo' | 'active' | 'done'

/**
 * One row of the vertical stepper. A completed step collapses to a plain green check + green label
 * (matching the DNS "Step completed" treatment) rather than a filled circle; todo/active steps keep
 * a numbered circle. `titleClassName` lets each wrapper size the heading to its own context.
 */
export const StepRow = ({
	index,
	state,
	title,
	titleClassName = 'text-base font-semibold',
	isLast,
	children
}: {
	index: number
	state: StepState
	title: string
	titleClassName?: string
	isLast?: boolean
	children?: ReactNode
}) => (
	<div className="relative flex gap-4">
		{!isLast && <div className="absolute left-[15px] top-[34px] bottom-0 w-0.5 bg-violet-400/40" />}
		<div className="z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center">
			{state === 'done' ? (
				<RiCheckLine className="h-6 w-6 text-green-500" />
			) : (
				<span
					className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
						state === 'active' ? 'bg-violet-400 text-t-email-blue' : 'bg-t-step-inactive text-[color:var(--muted-ink,#8B8AA5)]'
					}`}
				>
					{index}
				</span>
			)}
		</div>
		<div className={`flex-1 ${isLast ? '' : 'pb-6'}`}>
			<div className="flex min-h-[32px] items-center">
				<span className={`${titleClassName} ${state === 'done' ? 'text-green-600' : 'text-t-default'}`}>{title}</span>
			</div>
			{children && <div className="mt-3">{children}</div>}
		</div>
	</div>
)

/** Numbered sub-instructions, styled like the DNS setup list. */
export const InstructionList = ({ items }: { items: ReactNode[] }) => (
	<ol className="flex flex-col gap-2.5 !pl-0">
		{items.map((item, i) => (
			<li key={i} className="flex items-center gap-3">
				<span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-t-step-inactive text-[12px] font-bold text-t-email-blue">
					{i + 1}
				</span>
				<span className="text-[14px] text-t-checkbox-unactive">{item}</span>
			</li>
		))}
	</ol>
)

/** Inline "approve in the Shopify tab…" waiting state with a Cancel affordance. */
export const WaitingRow = ({ text, onCancel }: { text: string; onCancel: () => void }) => (
	<div className="flex flex-col gap-3">
		<div className="flex items-center gap-3">
			<DotMatrix size="sm" tone="inherit" className="flex-shrink-0 text-primary-500" />
			<span className="text-base text-t-checkbox-unactive">{text}</span>
		</div>
		<GButton
			color="neutral"
			label={SHOPIFY_COPY.cancel}
			className="h-[36px] self-start rounded-[8px] px-4"
			onClick={onCancel}
		/>
	</div>
)

/** Clean skeleton for the initial status fetch — replaces the old bare spinner. */
export const ShopifyConnectLoading = () => (
	<div className="flex flex-col gap-6" aria-busy="true">
		{[0, 1].map((i) => (
			<div key={i} className="flex animate-pulse gap-4">
				<div className="h-8 w-8 flex-shrink-0 rounded-full bg-t-step-inactive" />
				<div className="flex-1 space-y-2 pt-1">
					<div className="h-4 w-1/3 rounded bg-t-step-inactive" />
					<div className="h-3 w-2/3 rounded bg-bg-plan-fill" />
				</div>
			</div>
		))}
	</div>
)
